import {
  EarningsItemWithEntity,
  EconomicEventOutput,
  KeyDevsObjectWithEntity,
} from '@api';
import { Button } from '@design/components/button/Button';
import { TabLines } from '@design/components/tab-lines/TabLines';
import { Toggle } from '@design/components/toggle/Toggle';
import { useScrollListener } from '@helpers';
import { Row } from '@tanstack/react-table';
import { groupEntriesByDate } from '@terminal/hooks/utils/grouping-utils';
import { useEarningsFilter } from '@terminal/react-query-hooks/earnings/useEarningsFilter';
import {
  KEY_DEV_TYPES_EXCEPT_EARNINGS,
  useCompanyEvents,
} from '@terminal/react-query-hooks/events/useCompanyEvents';
import { useEconomicsEvents } from '@terminal/react-query-hooks/events/useEconomicsEvents';
import { useEarningsInsightsByEarningsId } from '@terminal/react-query-hooks/insights/useEarningsInsightsByEarningsId';
import { queryKeys } from '@terminal/routes/query-params';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { useWatchlist } from '@terminal/stores/use-watchlist/useWatchlist';
import {
  CalendarEvents,
  isEarningsEvent,
} from '@terminal/views/dashboard/calendar-widget/calendar-event-drawer/utils';
import {
  SubTabsType,
  useEarningsInsightDrawer,
} from '@terminal/widgets/earnings-insight-drawer/useEarningsInsightDrawer';
import { addDays, endOfDay, startOfDay } from 'date-fns';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { CountrySelector } from '../country-selector/CountrySelector';
import { EventsTable } from '../events-table/EventsTable';
import { useEventsColumns } from '../useEventsColumns';
import {
  CountryFilter,
  isDateBeyondThreeMonthsAndSunday,
  TabsType,
} from '../utils';
import styles from './EventTabs.module.scss';

export interface EventTabsProps {
  activeDate: Date;
  activeCountries: CountryFilter[];
  companyOnlyWatchlists: boolean;
  earningsOnlyWatchlists: boolean;
  forceIsTableLoading: boolean;
  setActiveCountries: (value: React.SetStateAction<CountryFilter[]>) => void;
  handleToggle: (checked: boolean, type: TabsType) => void;
  onDateChange: (date: Date) => void;
  setForceIsTableLoading: (isLoading: boolean) => void;
}

export const EventTabs = ({
  activeDate,
  companyOnlyWatchlists,
  earningsOnlyWatchlists,
  activeCountries,
  forceIsTableLoading,
  setForceIsTableLoading,
  setActiveCountries,
  handleToggle,
  onDateChange,
}: EventTabsProps) => {
  const { t } = useTranslation(['calendars']);
  const [searchParams, setSearchParams] = useSearchParams();
  const hasWatchlist = useWatchlist(state => !!state.lists.length);
  const [activeTab, setActiveTab] = useState(TabsType.Earnings);

  const TABS = useMemo(
    () => [
      {
        tabKey: TabsType.Earnings,
        tabLabel: t('calendar:earnings'),
      },
      {
        tabKey: TabsType.Economics,
        tabLabel: t('calendar:economic'),
      },
      {
        tabKey: TabsType.Company,
        tabLabel: t('calendar:company'),
      },
    ],
    []
  );

  useEffect(() => {
    const activeTabSearchParam = searchParams.get(
      queryKeys.activeCalendarTab
    ) as TabsType;

    if (Object.values(TabsType).includes(activeTabSearchParam)) {
      setActiveTab(activeTabSearchParam);
    } else {
      setActiveTab(TabsType.Earnings);
    }
  }, [searchParams]);

  const onTabChange = (_: number, tabKey: string) => {
    if (tabKey !== activeTab) {
      setSearchParams(params => {
        params.set(queryKeys.activeCalendarTab, tabKey);
        return params;
      });
    }
  };

  const {
    data: earningsQueryResult,
    isLoading: isEarningsLoading,
    isError: isEarningsError,
    refetch: earningRefetch,
    hasNextPage: hasEarningsNextPage,
    fetchNextPage: fetchEarningsNextPage,
  } = useEarningsFilter({
    body: {
      start_date: startOfDay(activeDate).toISOString(),
      end_date: endOfDay(activeDate).toISOString(),
      watchlists: {
        all: earningsOnlyWatchlists,
      },
      sort_by: {
        field: 'market_cap',
        desc: true,
      },
    },
    pageSize: 20,
  });

  const earningsData = useMemo(
    () =>
      earningsQueryResult?.pages.flatMap(
        (page: EarningsItemWithEntity[]) => page
      ) ?? ([] as EarningsItemWithEntity[]),
    [earningsQueryResult?.pages]
  );

  const groupedEarningsData = groupEntriesByDate<EarningsItemWithEntity>(
    earningsData,
    'reporting_date'
  );

  const {
    data: economicsQueryResult,
    isLoading: isEconomicsLoading,
    isError: isEconomicsError,
    refetch: economicRefetch,
    hasNextPage: hasEconomicsNextPage,
    fetchNextPage: fetchEconomicsNextPage,
  } = useEconomicsEvents({
    from: startOfDay(activeDate).toISOString(),
    to: endOfDay(activeDate).toISOString(),
    countries: activeCountries.map(country => country.value.value),
  });

  const economicData = useMemo(
    () =>
      economicsQueryResult?.pages.flatMap(page => page) ??
      ([] as EconomicEventOutput[]),
    [economicsQueryResult?.pages]
  );

  const groupedEconomicsEvents = groupEntriesByDate<EconomicEventOutput>(
    economicData,
    'date'
  );

  const {
    data: companiesQueryResult,
    isLoading: isCompanyLoading,
    isError: isCompanyError,
    refetch: companyRefetch,
    hasNextPage: hasCompanyNextPage,
    fetchNextPage: fetchCompanyNextPage,
  } = useCompanyEvents({
    filters: [
      {
        start_date: startOfDay(activeDate).toISOString(),
        end_date: endOfDay(activeDate).toISOString(),
        types: KEY_DEV_TYPES_EXCEPT_EARNINGS,
      },
    ],
    sorting: [
      {
        desc: false,
        id: 'effective_date',
      },
    ],
    watchlists: {
      all: companyOnlyWatchlists,
    },
  });

  const companiesData = useMemo(
    () =>
      companiesQueryResult?.pages.flatMap(page => page.key_devs) ??
      ([] as KeyDevsObjectWithEntity[]),
    [companiesQueryResult?.pages]
  );

  const groupedCompaniesData = groupEntriesByDate<KeyDevsObjectWithEntity>(
    companiesData,
    'effective_date'
  );

  const getEventsData = () => {
    switch (activeTab) {
      case TabsType.Earnings:
      default:
        return {
          data: groupedEarningsData,
          isLoading: isEarningsLoading,
          isError: isEarningsError,
          handleRefetch: () => earningRefetch(),
          hasNextPage: hasEarningsNextPage ?? false,
        };
      case TabsType.Economics:
        return {
          data: groupedEconomicsEvents,
          isLoading: isEconomicsLoading,
          isError: isEconomicsError,
          handleRefetch: () => economicRefetch(),
          hasNextPage: hasEconomicsNextPage ?? false,
        };
      case TabsType.Company:
        return {
          data: groupedCompaniesData,
          isLoading: isCompanyLoading,
          isError: isCompanyError,
          handleRefetch: () => companyRefetch(),
          hasNextPage: hasCompanyNextPage ?? false,
        };
    }
  };

  const isEconomicTab = activeTab === TabsType.Economics;
  const isEarningsTab = activeTab === TabsType.Earnings;
  const isCompanyTab = activeTab === TabsType.Company;
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const { openDrawer: openEarningsInsightDrawer } = useEarningsInsightDrawer();

  const { earningsInsightByEarningsId, isEarningsInsightsFetching } =
    useEarningsInsightsByEarningsId({
      earningsIds: earningsData.map(earning => earning.id),
      isEnabled: isEarningsTab,
    });

  const columns = useEventsColumns({
    activeTab,
    earningsInsightByEarningsId,
    isEarningsInsightsFetching,
  });

  const handleEarningsRowClick = (row: Row<CalendarEvents>) => {
    const event = row.original;

    if (!isEarningsEvent(event)) {
      return false;
    }

    const insight = earningsInsightByEarningsId.get(event.id);

    if (!insight) {
      return false;
    }

    openEarningsInsightDrawer({
      entityTag: insight.entity_tag,
      earningsId: insight.earnings_id,
      subtab:
        insight.type === 'preview' ? SubTabsType.Preview : SubTabsType.Recap,
      source: POSTHOG_TRACKING_SOURCES.EVENTS,
    });

    return true;
  };

  useScrollListener(
    tableContainerRef,
    () => {
      if (isEarningsTab && hasEarningsNextPage) {
        fetchEarningsNextPage();
      } else if (isCompanyTab && hasCompanyNextPage) {
        fetchCompanyNextPage();
      } else if (isEconomicTab && hasEconomicsNextPage) {
        fetchEconomicsNextPage();
      }

      return (
        (isEarningsTab && hasEarningsNextPage) ||
        (isCompanyTab && hasCompanyNextPage) ||
        (isEconomicTab && hasEconomicsNextPage)
      );
    },
    [
      isEarningsTab,
      hasEarningsNextPage,
      isCompanyTab,
      hasCompanyNextPage,
      isEconomicTab,
      hasEconomicsNextPage,
    ],
    { isGlobalScrollListened: false, offset: 20 }
  );

  return (
    <>
      <TabLines
        className={styles.styledTabLines}
        tabs={TABS}
        activeTab={activeTab}
        onChange={onTabChange}
      />
      <div className={styles.tabsContentWrapper}>
        {isEconomicTab ? (
          <CountrySelector
            activeCountries={activeCountries}
            setActiveCountries={setActiveCountries}
          />
        ) : (
          hasWatchlist && (
            <div
              className={styles.toggleWrapper}
              data-testid="only-watchlist-toggle"
            >
              <Toggle
                id="toggle-watchlist-only"
                leftLabel={t('calendar:table.toggleLabel')}
                isChecked={
                  isEarningsTab ? earningsOnlyWatchlists : companyOnlyWatchlists
                }
                onToggle={checked => handleToggle(checked, activeTab)}
              />
            </div>
          )
        )}
        <EventsTable
          activeTab={activeTab}
          activeDate={activeDate}
          columns={columns}
          tableDataSet={getEventsData()}
          forceIsTableLoading={forceIsTableLoading}
          setForceIsTableLoading={setForceIsTableLoading}
          tableContainerRef={tableContainerRef}
          onRowSelect={handleEarningsRowClick}
        />
        <div className={styles.bottomBar}>
          <Button
            className={styles.styledButton}
            variant="empty"
            size="small"
            iconName="ArrowLeft"
            label={t('calendar:previousDay')}
            onClick={() => onDateChange(addDays(activeDate, -1))}
          />
          <Button
            className={styles.styledButton}
            variant="empty"
            size="small"
            iconName="ArrowRight"
            iconPosition="right"
            label={t('calendar:nextDay')}
            disabled={isDateBeyondThreeMonthsAndSunday(activeDate)}
            onClick={() => onDateChange(addDays(activeDate, 1))}
          />
        </div>
      </div>
    </>
  );
};
